// src/components/admin/sponsors/SponsorApplicationsList.jsx
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  fetchSponsorApplications,
  fetchSponsorApplicationDetails,
  approveSponsorApplication,
  rejectSponsorApplication,
  suspendSponsor,
  unsuspendSponsor,
  clearAuditNav,
} from "@/store/slices/adminSlice";
import {
  Briefcase,
  CheckCircle, XCircle, Eye, Clock, Ban, Unlock, AlertTriangle,
  ChevronDown, ArrowUpDown,
} from "lucide-react";
import Button from "@/components/common/Button";
import Badge from "@/components/common/Badge";
import Modal from "@/components/common/Modal";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import TextArea from "@/components/common/TextArea";
import TextInput from "@/components/common/TextInput";
import FilterPanel from "@/components/common/FilterPanel";
import Table from "@/components/common/Table";
import Pagination from "@/components/common/Pagination";
import EntityAuditHistory from "@/components/admin/audit/EntityAuditHistory";
import { toast } from "react-toastify";

const STATUS_OPTIONS = [
  { value: "", label: "All Status" },
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "suspended", label: "Suspended" },
];

const SEARCH_FIELDS = [
  { value: "search_company", label: "Company" },
  { value: "search_email", label: "Email" },
];

const SORT_OPTIONS = [
  { value: "recent", label: "Recent" },
  { value: "company", label: "Company" },
];

const SPONSORSHIP_TYPE_LABELS = {
  funded: "Funded",
  kind: "In-Kind",
  funded_and_kind: "Funded & In-Kind",
};

const CATEGORY_LABELS = {
  hydration: "Hydration",
  medals: "Medals",
  nutrition: "Nutrition",
  apparels: "Apparels",
  gears: "Gears",
  spares: "Spares",
  accessories: "Accessories",
  registration_fees: "Registration Fees",
  insurance: "Insurance",
  bikes: "Bikes",
  certificates: "Certificates",
  route_signages: "Route Signages",
  branding: "Branding",
  finance_and_support_vehicles: "Finance & Support Vehicles",
};

const formatSponsorshipType = (type) => SPONSORSHIP_TYPE_LABELS[type] || type;
const formatCategory = (cat) => CATEGORY_LABELS[cat] || cat;

// ── InlineDropdown (matches UsersList / OrganizersList pattern) ────
const InlineDropdown = ({
  options,
  value,
  onChange,
  className = "",
  compact = false,
}) => {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const containerRef = useRef(null);

  const selectedLabel = options.find((o) => o.value === value)?.label || "";

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (opt) => {
    onChange({ target: { value: opt.value } });
    setOpen(false);
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(!open);
        }}
        className={`flex items-center justify-between gap-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors ${
          compact ? "px-2 py-1 text-sm" : "px-3 py-2 text-sm w-full"
        }`}
      >
        <span className="truncate">{selectedLabel}</span>
        <ChevronDown
          className={`w-4 h-4 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div
          className="absolute z-60 mt-1 w-full min-w-max bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-1 max-h-48 overflow-y-auto animate-in fade-in slide-in-from-top-2 duration-150"
          onClick={(e) => e.stopPropagation()}
        >
          {options.map((opt, i) => (
            <div
              key={opt.value}
              onMouseEnter={() => setHighlight(i)}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelect(opt);
              }}
              className={`px-3 py-2 text-sm cursor-pointer transition-colors ${
                value === opt.value
                  ? "bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 font-medium"
                  : i === highlight
                    ? "bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                    : "text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              }`}
            >
              {opt.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────
const SponsorApplicationsList = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    sponsorApplications,
    sponsorsPage,
    sponsorsTotalPages,
    sponsorsLimit,
    isLoadingSponsors,
    isUpdating,
    currentSponsorApplication,
  } = useSelector((state) => state.admin);

  // Modal / action state
  const [selectedSponsor, setSelectedSponsor] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [showUnsuspendConfirm, setShowUnsuspendConfirm] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [suspendReason, setSuspendReason] = useState("");
  const [suspendDuration, setSuspendDuration] = useState("");
  const [activeTab, setActiveTab] = useState("details");

  // Filter state
  const [filters, setFilters] = useState({
    search: "",
    searchField: "search_company",
    status: "",
  });

  // Sort state
  const [sorting, setSorting] = useState({
    field: "recent",
    order: "desc",
  });

  const [debouncedSearch, setDebouncedSearch] = useState("");

  // ─── Audit Log Deep-Link: auto-open details when ?id= is in URL ───
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const deepLinkId = params.get('id');
    if (deepLinkId) {
      navigate(location.pathname, { replace: true });
      dispatch(fetchSponsorApplicationDetails(deepLinkId));
    }
    return () => { dispatch(clearAuditNav()); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (currentSponsorApplication && currentSponsorApplication._id) {
      setSelectedSponsor(currentSponsorApplication);
      setShowDetailsModal(true);
    }
  }, [currentSponsorApplication]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(filters.search);
    }, 500);
    return () => clearTimeout(timer);
  }, [filters.search]);

  // Build API params and fetch
  const fetchList = useCallback(
    (page = 1) => {
      const apiFilters = {
        page,
        limit: sponsorsLimit,
        status: filters.status || undefined,
        sort: sorting.field,
        order: sorting.order,
      };

      if (debouncedSearch.trim()) {
        apiFilters[filters.searchField] = debouncedSearch.trim();
      }

      dispatch(fetchSponsorApplications(apiFilters));
    },
    [dispatch, sponsorsLimit, debouncedSearch, filters.searchField, filters.status, sorting]
  );

  useEffect(() => {
    fetchList(1);
  }, [fetchList]);

  const handlePageChange = (newPage) => {
    fetchList(newPage);
  };

  // ── Handlers ──────────────────────────────────────────────────

  const handleSortToggle = (field) => {
    setSorting((prev) => ({
      field,
      order: prev.field === field && prev.order === "desc" ? "asc" : "desc",
    }));
  };

  const handleClearFilters = () => {
    setFilters({ search: "", searchField: "search_company", status: "" });
  };

  const hasActiveFilters =
    filters.search.trim() !== "" ||
    filters.status !== "" ||
    filters.searchField !== "search_company";

  const handleApprove = async () => {
    if (!selectedSponsor) return;
    try {
      await dispatch(approveSponsorApplication(selectedSponsor._id)).unwrap();
      toast.success("Sponsor approved successfully");
      setShowApproveConfirm(false);
      setSelectedSponsor(null);
      fetchList(sponsorsPage);
    } catch (error) {
      toast.error(error.message || "Failed to approve sponsor");
    }
  };

  const handleReject = async () => {
    if (!selectedSponsor || !rejectionReason.trim()) {
      toast.error("Please provide a rejection reason");
      return;
    }
    try {
      await dispatch(
        rejectSponsorApplication({
          sponsorId: selectedSponsor._id,
          rejection_reason: rejectionReason,
        })
      ).unwrap();
      toast.success("Sponsor rejected");
      setShowRejectModal(false);
      setRejectionReason("");
      setSelectedSponsor(null);
      fetchList(sponsorsPage);
    } catch (error) {
      toast.error(error.message || "Failed to reject sponsor");
    }
  };

  const handleSuspend = async () => {
    const sponsorId = selectedSponsor.sponsor_id?._id || selectedSponsor.sponsor_id;
    if (!sponsorId) {
      toast.error("Sponsor record not found — application may not have been approved yet");
      return;
    }
    if (!suspendReason.trim()) {
      toast.error("Please provide a suspension reason");
      return;
    }
    try {
      await dispatch(
        suspendSponsor({
          sponsorId,
          reason: suspendReason,
          duration_days: suspendDuration ? parseInt(suspendDuration) : undefined,
        })
      ).unwrap();
      toast.success("Sponsor suspended successfully");
      setShowSuspendModal(false);
      setSuspendReason("");
      setSuspendDuration("");
      fetchList(sponsorsPage);
    } catch (error) {
      toast.error(error.message || "Failed to suspend user");
    }
  };

  const handleUnsuspend = async () => {
    const sponsorId = selectedSponsor.sponsor_id?._id || selectedSponsor.sponsor_id;
    if (!sponsorId) {
      toast.error("Sponsor record not found");
      return;
    }
    try {
      await dispatch(unsuspendSponsor(sponsorId)).unwrap();
      toast.success("User unsuspended successfully");
      setShowUnsuspendConfirm(false);
      fetchList(sponsorsPage);
    } catch (error) {
      toast.error(error.message || "Failed to unsuspend user");
    }
  };

  // ── Helpers ───────────────────────────────────────────────────

  const getStatusBadge = (status) => {
    const config = {
      pending: { tone: "info", label: "Pending", icon: <Clock className="w-3 h-3" /> },
      approved: { tone: "success", label: "Approved", icon: <CheckCircle className="w-3 h-3" /> },
      rejected: { tone: "danger", label: "Rejected", icon: <XCircle className="w-3 h-3" /> },
      suspended: { tone: "warning", label: "Suspended", icon: <Ban className="w-3 h-3" /> },
    };
    const c = config[status] || config.pending;
    return (
      <Badge tone={c.tone}>
        <span className="flex items-center gap-1">{c.icon} {c.label}</span>
      </Badge>
    );
  };

  const openDetails = (sponsor) => {
    setSelectedSponsor(sponsor);
    setShowDetailsModal(true);
    setActiveTab("details");
  };

  const openApproveConfirm = (sponsor) => {
    setSelectedSponsor(sponsor);
    setShowApproveConfirm(true);
  };

  const openRejectModal = (sponsor) => {
    setSelectedSponsor(sponsor);
    setShowRejectModal(true);
    setRejectionReason("");
  };

  // ── Render ────────────────────────────────────────────────────

  return (
    <div className="space-y-4">
      {/* Filters Panel */}
      <FilterPanel
        hasActiveFilters={hasActiveFilters}
        onClearFilters={handleClearFilters}
        defaultOpen={true}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Search Section */}
          <div className="lg:col-span-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Search Sponsors
            </label>
            <div className="flex gap-2">
              <InlineDropdown
                options={SEARCH_FIELDS}
                value={filters.searchField}
                onChange={(e) =>
                  setFilters({ ...filters, searchField: e.target.value })
                }
                className="w-1/3"
              />
              <input
                type="text"
                value={filters.search}
                onChange={(e) =>
                  setFilters({ ...filters, search: e.target.value })
                }
                placeholder="Enter search term..."
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Status
            </label>
            <InlineDropdown
              options={STATUS_OPTIONS}
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            />
          </div>
        </div>
      </FilterPanel>

      {/* Sorting Panel */}
      <FilterPanel
        title="Sorting"
        icon={ArrowUpDown}
        hasActiveFilters={sorting.field !== "recent"}
        onClearFilters={() => setSorting({ field: "recent", order: "desc" })}
        defaultOpen={false}
      >
        <div className="grid grid-cols-2 md:grid-cols-2 gap-3">
          {SORT_OPTIONS.map((opt) => (
            <div key={opt.value}>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                {opt.label}
              </label>
              <button
                onClick={() => handleSortToggle(opt.value)}
                className={`w-full px-3 py-2 text-xs font-medium rounded-lg border transition-all ${
                  sorting.field === opt.value
                    ? "bg-green-50 dark:bg-green-900/20 border-green-500 text-green-700 dark:text-green-400"
                    : "bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400"
                } hover:shadow-sm`}
              >
                {sorting.field === opt.value
                  ? `${sorting.order === "desc" ? "↓" : "↑"}`
                  : "Not Sorted"}
              </button>
            </div>
          ))}
        </div>
      </FilterPanel>

      {/* Results Count */}
      <div className="flex justify-between items-center px-1">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          {isLoadingSponsors ? (
            "Loading sponsors..."
          ) : (
            <>Showing {sponsorApplications.length} sponsors</>
          )}
        </div>
      </div>

      {/* Table */}
      {isLoadingSponsors && sponsorApplications.length === 0 ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600"></div>
        </div>
      ) : (
        <Table
          columns={[
            { key: "company", title: "Company" },
            { key: "contact", title: "Contact" },
            { key: "type", title: "Type" },
            { key: "status", title: "Status" },
            { key: "date", title: "Applied On" },
            { key: "actions", title: "Actions" },
          ]}
          data={sponsorApplications}
          renderRow={(sponsor) => (
            <tr
              key={sponsor._id}
              className="hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors"
            >
              <td className="px-4 py-3">
                <p className="font-medium text-gray-900 dark:text-gray-100">
                  {sponsor.company_name}
                </p>
                <p className="text-sm text-gray-500">{sponsor.zones?.join(", ")}</p>
              </td>
              <td className="px-4 py-3">
                <p className="text-sm text-gray-800 dark:text-gray-100">
                  {sponsor.applicant_email}
                </p>
                <p className="text-sm text-gray-500">{sponsor.applicant_phone}</p>
              </td>
              <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                {formatSponsorshipType(sponsor.sponsorship_type)}
              </td>
              <td className="px-4 py-3">
                {getStatusBadge(sponsor.sponsor_id?.is_active === false ? "suspended" : sponsor.status)}
              </td>
              <td className="px-4 py-3 text-sm text-gray-500">
                {new Date(sponsor.created_at).toLocaleDateString("en-IN", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })}
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => openDetails(sponsor)}
                    size="sm"
                    variant="outline"
                    className="h-8 px-3 text-xs"
                  >
                    View
                  </Button>
                </div>
              </td>
            </tr>
          )}
        />
      )}

      {/* Empty State */}
      {!isLoadingSponsors && sponsorApplications.length === 0 && (
        <div className="text-center py-20 bg-white dark:bg-gray-800 rounded-lg border border-dashed border-gray-300 dark:border-gray-700">
          <Briefcase className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 dark:text-gray-400">
            No sponsors found matching your criteria.
          </p>
        </div>
      )}

      <Pagination
        page={sponsorsPage}
        totalPages={sponsorsTotalPages}
        onChange={handlePageChange}
      />

      {/* ── Modals ─────────────────────────────────────────────── */}

      {/* Details Modal */}
      <Modal
        open={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        title="Sponsor Application Details"
      >
        {selectedSponsor && (
          <>
            <div className="flex border-b border-gray-200 dark:border-gray-700 mb-4">
              <button
                className={`px-4 py-2 text-sm font-medium ${
                  activeTab === "details"
                    ? "text-green-600 border-b-2 border-green-600"
                    : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                }`}
                onClick={() => setActiveTab("details")}
              >
                Details
              </button>
              <button
                className={`px-4 py-2 text-sm font-medium ${
                  activeTab === "history"
                    ? "text-green-600 border-b-2 border-green-600"
                    : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                }`}
                onClick={() => setActiveTab("history")}
              >
                Audit History
              </button>
            </div>
            {activeTab === "details" ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Company</p>
                <p className="font-medium text-gray-800 dark:text-gray-100">
                  {selectedSponsor.company_name}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Sponsorship Type</p>
                <p className="font-medium text-gray-800 dark:text-gray-100">
                  {formatSponsorshipType(selectedSponsor.sponsorship_type)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Duration Interest</p>
                <p className="font-medium text-gray-800 dark:text-gray-100">
                  {selectedSponsor.duration_interest || "N/A"}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Email</p>
                <p className="font-medium text-gray-800 dark:text-gray-100">
                  {selectedSponsor.applicant_email}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Phone</p>
                <p className="font-medium text-gray-800 dark:text-gray-100">
                  {selectedSponsor.applicant_phone}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">GST Number</p>
                <p className="font-medium text-gray-800 dark:text-gray-100">
                  {selectedSponsor.gst_number}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Status</p>
                {getStatusBadge(selectedSponsor.sponsor_id?.is_active === false ? "suspended" : selectedSponsor.status)}
              </div>
              {selectedSponsor.reviewed_by && (
                <div>
                  <p className="text-sm text-gray-500">Reviewed By</p>
                  <p className="font-medium text-gray-800 dark:text-gray-100">
                    {selectedSponsor.reviewed_by.name}
                  </p>
                  <p className="text-xs text-gray-500">
                    (@{selectedSponsor.reviewed_by.username})
                  </p>
                </div>
              )}
              {selectedSponsor.reviewed_at && (
                <div>
                  <p className="text-sm text-gray-500">Reviewed At</p>
                  <p className="font-medium text-gray-800 dark:text-gray-100">
                    {new Date(selectedSponsor.reviewed_at).toLocaleDateString(
                      "en-IN",
                      {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      }
                    )}
                  </p>
                </div>
              )}
            </div>

            {selectedSponsor.categories && selectedSponsor.categories.length > 0 && (
              <div>
                <p className="text-sm text-gray-500 mb-1">Categories</p>
                <div className="flex flex-wrap gap-2">
                  {selectedSponsor.categories.map((cat, index) => (
                    <Badge key={index} tone="neutral">{formatCategory(cat)}</Badge>
                  ))}
                </div>
              </div>
            )}

            {selectedSponsor.zones && selectedSponsor.zones.length > 0 && (
              <div>
                <p className="text-sm text-gray-500 mb-1">Zones</p>
                <div className="flex flex-wrap gap-2">
                  {selectedSponsor.zones.map((zone, index) => (
                    <Badge key={index} tone="info">{zone}</Badge>
                  ))}
                </div>
              </div>
            )}

            {selectedSponsor.rejection_reason && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm font-medium text-red-700 dark:text-red-400 mb-1">
                  Rejection Reason
                </p>
                <p className="text-red-600 dark:text-red-300">
                  {selectedSponsor.rejection_reason}
                </p>
              </div>
            )}

            {selectedSponsor.sponsor_id?.is_active === false && selectedSponsor.sponsor_id?.suspension && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-100 dark:border-red-900/30 text-sm mt-4">
                <div className="font-bold text-red-800 dark:text-red-400 flex items-center gap-2 mb-1">
                  <AlertTriangle className="w-4 h-4" /> Suspended
                </div>
                <p className="text-gray-700 dark:text-gray-300 mb-2">"{selectedSponsor.sponsor_id.suspension.reason}"</p>
                {selectedSponsor.sponsor_id.suspension.suspended_until && (
                  <div className="text-xs text-gray-500">
                    Until: {new Date(selectedSponsor.sponsor_id.suspension.suspended_until).toLocaleDateString()}
                  </div>
                )}
              </div>
            )}

            {selectedSponsor.status === "pending" && (
              <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <Button
                  onClick={() => {
                    setShowDetailsModal(false);
                    openApproveConfirm(selectedSponsor);
                  }}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Approve
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowDetailsModal(false);
                    openRejectModal(selectedSponsor);
                  }}
                  className="flex-1 border-red-200 text-red-600 hover:bg-red-50"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Reject
                </Button>
              </div>
            )}

            {selectedSponsor.status === "approved" && (
              <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                {selectedSponsor.sponsor_id?.is_active === false ? (
                  <Button
                    variant="outline"
                    onClick={() => setShowUnsuspendConfirm(true)}
                    className="flex-1 border-yellow-200 text-yellow-600 hover:bg-yellow-50"
                  >
                    <Unlock className="w-4 h-4 mr-2" />
                    Unsuspend Sponsor
                  </Button>
                ) : (
                  <Button
                    variant="warning"
                    onClick={() => setShowSuspendModal(true)}
                    className="flex-1 shadow-sm"
                  >
                    <AlertTriangle className="w-4 h-4 mr-2" />
                    Suspend User
                  </Button>
                )}
              </div>
            )}
            
          </div>
            ) : (
              <div className="p-4">
                <EntityAuditHistory entityId={selectedSponsor._id} entityType="Sponsor" />
              </div>
            )}
          </>
        )}
      </Modal>

      {/* Approve Confirmation */}
      <ConfirmDialog
        open={showApproveConfirm}
        onClose={() => setShowApproveConfirm(false)}
        onConfirm={handleApprove}
        title="Approve Sponsor"
        message={`Are you sure you want to approve "${selectedSponsor?.company_name}"? They will receive login credentials via email.`}
        confirmText="Approve"
        loading={isUpdating}
      />

      <ConfirmDialog
        open={showUnsuspendConfirm}
        onClose={() => setShowUnsuspendConfirm(false)}
        onConfirm={handleUnsuspend}
        title="Unsuspend User"
        message={`Are you sure you want to unsuspend "${selectedSponsor?.company_name}"? They will regain access immediately.`}
        confirmText="Unsuspend"
        loading={isUpdating}
      />

      <Modal
        open={showSuspendModal}
        onClose={() => setShowSuspendModal(false)}
        title="Suspend User"
      >
        <div className="space-y-4">
           <p className="text-gray-600 dark:text-gray-400">
             Suspend access for "{selectedSponsor?.company_name}"? This will prevent the user from logging in.
           </p>
           <TextArea
              label="Suspension Reason *"
              value={suspendReason}
              onChange={(e) => setSuspendReason(e.target.value)}
              placeholder="Reason for suspension..."
              rows={3}
           />
           <TextInput
              label="Duration (Days)"
              type="number"
              value={suspendDuration}
              onChange={(e) => setSuspendDuration(e.target.value)}
              placeholder="Optional (leave empty for indefinite)"
              min="1"
           />
           <div className="flex gap-3">
             <Button
               variant="outline"
               onClick={() => setShowSuspendModal(false)}
               className="flex-1"
             >
               Cancel
             </Button>
             <Button
               onClick={handleSuspend}
               loading={isUpdating}
               className="flex-1 bg-red-600 hover:bg-red-700"
             >
               Suspend User
             </Button>
           </div>
        </div>
      </Modal>

      {/* Reject Modal */}
      <Modal
        open={showRejectModal}
        onClose={() => setShowRejectModal(false)}
        title="Reject Sponsor Application"
      >
        <div className="space-y-4">
          <p className="text-gray-600 dark:text-gray-400">
            Please provide a reason for rejecting "{selectedSponsor?.company_name}".
          </p>
          <TextArea
            label="Rejection Reason *"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="Explain why the application is being rejected..."
            rows={4}
          />
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setShowRejectModal(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleReject}
              loading={isUpdating}
              className="flex-1 bg-red-600 hover:bg-red-700"
            >
              Reject Application
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default SponsorApplicationsList;
